import React, { useEffect, useState, useRef } from "react";
import { FileText, Loader2, ImageOff, ExternalLink } from "lucide-react";
import { ADMIN_TOKEN_KEY } from "@/lib/authApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Auth-gated receipt thumbnail. Because <img src> cannot send Authorization headers,
 * we fetch as a blob with the Bearer token, then hand a blob: URL to <img>.
 *
 * Props:
 *   receiptUrl        – full URL returned by the API (…/api/uploads/receipts/{fname})
 *   contentType       – image/jpeg | image/png | image/webp | application/pdf
 *   originalName      – for the tooltip / a11y
 */
export function ReceiptThumb({ receiptUrl, contentType, originalName }) {
  const [state, setState] = useState({ status: "idle", blobUrl: null, error: null });
  const abortRef = useRef(null);

  useEffect(() => {
    if (!receiptUrl) return;
    // Only auto-fetch for image types. PDFs stay as an icon that opens in a new tab on click.
    if (!contentType?.startsWith("image/")) return;
    const fname = receiptUrl.split("/").pop();
    if (!fname) return;
    const controller = new AbortController();
    abortRef.current = controller;
    setState({ status: "loading", blobUrl: null, error: null });
    fetch(`${API}/uploads/receipts/${fname}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY) || ""}` },
      signal: controller.signal,
    })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.blob(); })
      .then((b) => setState({ status: "ok", blobUrl: URL.createObjectURL(b), error: null }))
      .catch((e) => { if (e.name !== "AbortError") setState({ status: "err", blobUrl: null, error: e.message }); });
    return () => {
      controller.abort();
      setState((prev) => {
        if (prev.blobUrl) URL.revokeObjectURL(prev.blobUrl);
        return { status: "idle", blobUrl: null, error: null };
      });
    };
  }, [receiptUrl, contentType]);

  const openFull = async (e) => {
    e.preventDefault();
    if (!receiptUrl) return;
    const fname = receiptUrl.split("/").pop();
    try {
      const r = await fetch(`${API}/uploads/receipts/${fname}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY) || ""}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const w = window.open(url, "_blank", "noopener,noreferrer");
      // Best-effort revoke after the tab has a chance to load.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      if (!w) window.location.href = url;
    } catch {}
  };

  if (!receiptUrl) {
    return <span className="mono-label" style={{ color: "var(--muted)" }}>—</span>;
  }

  if (contentType === "application/pdf") {
    return (
      <button
        onClick={openFull}
        title={originalName || "PDF receipt · open in new tab"}
        data-testid="receipt-thumb-pdf"
        className="w-10 h-10 flex items-center justify-center hairline hover:border-[var(--red)] transition-colors"
        style={{ background: "var(--panel-2)" }}
      >
        <FileText size={16} style={{ color: "var(--red-2)" }} />
      </button>
    );
  }

  if (state.status === "loading") {
    return (
      <div className="w-10 h-10 flex items-center justify-center hairline" style={{ background: "var(--panel-2)" }}>
        <Loader2 size={12} className="animate-spin" style={{ color: "var(--taupe)" }} />
      </div>
    );
  }

  if (state.status === "err") {
    return (
      <div className="w-10 h-10 flex items-center justify-center hairline" title={state.error || "Failed to load"} style={{ background: "var(--panel-2)" }}>
        <ImageOff size={14} style={{ color: "var(--taupe)" }} />
      </div>
    );
  }

  return (
    <button
      onClick={openFull}
      title={originalName || "Open receipt"}
      data-testid="receipt-thumb-img"
      className="w-10 h-10 hairline overflow-hidden hover:border-[var(--red)] transition-colors relative group"
      style={{ background: "var(--panel-2)" }}
    >
      {state.blobUrl ? (
        <>
          <img src={state.blobUrl} alt={originalName || "Receipt"} className="w-full h-full object-cover" />
          <span className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity">
            <ExternalLink size={12} className="text-white" />
          </span>
        </>
      ) : null}
    </button>
  );
}
